import type { SourceResponse } from "@patent-studio/api-types";
import { useDisclosure } from "@/components/ui/Disclosure";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { FoldingCard } from "./FoldingCard";
import { ReadStatusChip, readOf } from "./ReadStatusChip";

function SourceRow({ source }: { source: SourceResponse }) {
  return (
    <li
      data-testid="source-row"
      className="flex items-center gap-2.5 border-t border-border-subtle py-[7px]"
    >
      <span className="min-w-0 truncate text-detail font-semibold">
        {source.name}
      </span>
      <span className="min-w-0 flex-1 text-[12px] text-muted-foreground">
        {source.reason === null
          ? source.slot
          : `${source.slot} – ${source.reason}`}
      </span>
      {source.used && <ReadStatusChip read={readOf(source)} />}
    </li>
  );
}

function SourceList({
  title,
  testId,
  sources,
}: {
  title: string;
  testId: string;
  sources: readonly SourceResponse[];
}) {
  return (
    <div data-testid={testId} className="mt-3">
      <MicroLabel as="h3" className="mb-1 block">
        {title} ({sources.length})
      </MicroLabel>
      <ul>
        {sources.map((source) => (
          <SourceRow key={source.file_id} source={source} />
        ))}
      </ul>
    </div>
  );
}

/**
 * The files a stage read its output from, and those it left out with why, folded under the
 * stage's heading. An attorney cannot trust an output whose inputs they cannot see.
 */
export function SourcesPanel({
  stage,
  sources,
}: {
  /** The stage's name, as the rail names it. */
  stage: string;
  sources: readonly SourceResponse[];
}) {
  const fold = useDisclosure();
  const used = sources.filter((s) => s.used);
  const left = sources.filter((s) => !s.used);
  return (
    <FoldingCard
      fold={fold}
      testId="sources-panel"
      className="mt-3.5"
      title={
        <span className="text-[13px] font-bold">
          Sources used during {stage}
        </span>
      }
      meta={
        <span className="text-[12px] text-muted-foreground">
          Used ({used.length}) · Not used ({left.length})
        </span>
      }
    >
      <SourceList title="Used" testId="sources-used" sources={used} />
      {left.length > 0 && (
        <SourceList title="Not used" testId="sources-not-used" sources={left} />
      )}
    </FoldingCard>
  );
}
